/**
 * Offline-first sync client (spec §12). Local progress is the source of truth;
 * the server (D1) is a mirror. Every network call degrades gracefully — if the
 * backend is unreachable, the game keeps working entirely from localStorage.
 */
import { progress, type ProgressData } from "../store/progress";
import { settings, type Settings } from "../store/settings";
import { mergeProgress } from "./merge";
import { backupCode as localBackupCode } from "./backupCode";

const API = "/api";
const PID_KEY = "cm.playerId";
const PUSH_DEBOUNCE = 1500;

export interface SyncState {
  online: boolean;
  playerId: string | null;
  lastSync: number;
}

const state: SyncState = { online: false, playerId: null, lastSync: 0 };
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let unsub: (() => void) | null = null;

export function syncState(): Readonly<SyncState> {
  return state;
}

function getPlayerId(): string | null {
  try {
    return localStorage.getItem(PID_KEY);
  } catch {
    return null;
  }
}

function setPlayerId(id: string): void {
  state.playerId = id;
  try {
    localStorage.setItem(PID_KEY, id);
  } catch {
    /* ignore */
  }
}

function clearPlayerId(): void {
  state.playerId = null;
  try {
    localStorage.removeItem(PID_KEY);
  } catch {
    /* ignore */
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (state.playerId) headers["X-Player-Id"] = state.playerId;
  const res = await fetch(`${API}${path}`, { ...init, headers: { ...headers, ...init?.headers } });
  if (!res.ok) throw new Error(`api ${path} -> ${res.status}`);
  state.online = true;
  return (await res.json()) as T;
}

/** Ensure we have an anonymous player id, creating one server-side if needed. */
export async function ensureSession(): Promise<string | null> {
  const existing = getPlayerId();
  if (existing) {
    state.playerId = existing;
    return existing;
  }
  try {
    const locale = (navigator.language || "en").slice(0, 2);
    const { playerId } = await api<{ playerId: string }>("/session", {
      method: "POST",
      body: JSON.stringify({ locale }),
    });
    setPlayerId(playerId);
    return playerId;
  } catch {
    state.online = false;
    return null;
  }
}

/** Pull server progress + settings; merge progress, adopt newer settings. */
export async function pull(): Promise<void> {
  if (!state.playerId) return;
  try {
    const { progress: row, settings: srow } = await api<{
      progress: { data: string } | null;
      settings: { data: string; updated_at: number } | null;
    }>("/profile");
    if (row?.data) {
      const remote = JSON.parse(row.data) as ProgressData;
      progress.replace(mergeProgress(progress.snapshot as ProgressData, remote));
    }
    if (srow?.data) {
      const remote = JSON.parse(srow.data) as Settings;
      // Settings are single-valued — adopt the newer side by timestamp.
      if ((remote.updatedAt ?? 0) > settings.snapshot.updatedAt) settings.replace(remote);
    }
    state.lastSync = Date.now();
  } catch (e) {
    // Orphaned player id (e.g. the server's data was reset) → re-register and
    // push the local state so this device keeps syncing instead of going dark.
    if (String(e).includes("-> 401")) {
      clearPlayerId();
      const id = await ensureSession();
      if (id) {
        await push();
        await pushSettings();
      }
    } else {
      state.online = false;
    }
  }
}

/** Push local progress to the server (debounced). */
export function schedulePush(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => void push(), PUSH_DEBOUNCE);
}

export async function push(): Promise<void> {
  if (!state.playerId) return;
  const data = progress.snapshot as ProgressData;
  try {
    await api("/progress", {
      method: "PUT",
      body: JSON.stringify({ data: JSON.stringify(data), updated_at: data.updatedAt }),
    });
    state.lastSync = Date.now();
  } catch {
    state.online = false;
  }
}

let settingsTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleSettingsPush(): void {
  if (settingsTimer) clearTimeout(settingsTimer);
  settingsTimer = setTimeout(() => void pushSettings(), PUSH_DEBOUNCE);
}

export async function pushSettings(): Promise<void> {
  if (!state.playerId) return;
  const data = settings.snapshot;
  try {
    await api("/settings", {
      method: "PUT",
      body: JSON.stringify({ data: JSON.stringify(data), updated_at: data.updatedAt }),
    });
    state.lastSync = Date.now();
  } catch {
    state.online = false;
  }
}

// ---- Anonymous handle (leaderboard display name) ----
const HANDLE_KEY = "cm.handle";
const HANDLE_WORDS = ["Nova", "Nebula", "Quasar", "Comet", "Pulsar", "Orbit", "Astro", "Lumen"];

export function playerHandle(): string {
  // A custom display name (set in Settings) always wins.
  const custom = settings.snapshot.displayName.trim();
  if (custom) return custom.slice(0, 24);
  try {
    const existing = localStorage.getItem(HANDLE_KEY);
    if (existing) return existing;
  } catch {
    /* ignore */
  }
  // Deterministic-ish from the player id when available, else fixed fallback.
  const id = state.playerId ?? "anon";
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const handle = `${HANDLE_WORDS[h % HANDLE_WORDS.length]}-${(h % 9000) + 1000}`;
  try {
    localStorage.setItem(HANDLE_KEY, handle);
  } catch {
    /* ignore */
  }
  return handle;
}

export type NameResult = "ok" | "taken" | "invalid" | "offline";

/**
 * Reserve a unique display name on the server. Returns "taken" if another
 * player already owns it, "invalid" if it fails the format rules, or "offline"
 * if the backend is unreachable (the name can't be verified). An empty name
 * releases the current reservation.
 */
export async function claimDisplayName(name: string): Promise<NameResult> {
  const id = state.playerId ?? (await ensureSession());
  if (!id) return "offline";
  try {
    const res = await fetch(`${API}/name`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Player-Id": id },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      state.online = true;
      return "ok";
    }
    if (res.status === 409) {
      state.online = true;
      return "taken";
    }
    if (res.status === 400) {
      state.online = true;
      return "invalid";
    }
    return "offline"; // 503 (db unconfigured) or other server error
  } catch {
    state.online = false;
    return "offline";
  }
}

export interface RunSubmit {
  mode: "endless" | "daily";
  ref: string;
  score: number;
  maxTier: number;
  drops: number;
  durationS: number;
  /** Board width (4/5/6) — splits the leaderboard so wide-board scores
   * compete only with other wide-board scores. */
  cols: number;
}

/** Submit a run for the leaderboard (offline-safe). */
export async function submitRun(run: RunSubmit): Promise<void> {
  if (!state.playerId) return;
  try {
    await api("/run", {
      method: "POST",
      body: JSON.stringify({
        mode: run.mode,
        ref: run.ref,
        cols: run.cols,
        score: run.score,
        max_tier: run.maxTier,
        drops: run.drops,
        duration_s: run.durationS,
        name: playerHandle(),
        // Equipped achievement-title id; only sent when the player actually
        // owns it (defense against stale settings after a reset).
        title:
          settings.snapshot.activeTitle &&
          progress.hasAchievement(settings.snapshot.activeTitle)
            ? settings.snapshot.activeTitle
            : null,
      }),
    });
  } catch {
    state.online = false;
  }
}

export interface LeaderEntry {
  name: string | null;
  title: string | null;
  score: number;
  max_tier: number;
  created_at: number;
}

export type LeaderPeriod = "all" | "month" | "week" | "today";
/** 0 = all widths combined; 4/5/6 = filtered to that single width. */
export type LeaderCols = 0 | 4 | 5 | 6;

export async function fetchLeaderboard(
  mode: string,
  ref: string,
  period: LeaderPeriod = "all",
  cols: LeaderCols = 0,
): Promise<LeaderEntry[] | null> {
  try {
    const colsParam = cols ? `&cols=${cols}` : "";
    const res = await api<{ entries: LeaderEntry[] }>(
      `/leaderboard?mode=${encodeURIComponent(mode)}&ref=${encodeURIComponent(ref)}&period=${period}${colsParam}`,
    );
    return res.entries;
  } catch {
    state.online = false;
    return null;
  }
}

/** Fetch today's daily config from the server (falls back to local derivation). */
export async function fetchDaily(): Promise<{ date: string; seed: string } | null> {
  try {
    return await api<{ date: string; seed: string }>("/daily");
  } catch {
    state.online = false;
    return null;
  }
}

/** Get a transferable backup code for this device's progress. */
export async function exportBackup(): Promise<string | null> {
  // The code is a pure function of the anonymous player UUID, so it can be
  // produced locally — no live server is required (the old server round-trip
  // failed whenever the backend/D1 was unavailable). We only need a player id;
  // adopt/create one if we don't have it yet.
  const id = getPlayerId() ?? (await ensureSession());
  if (!id) return null;
  return localBackupCode(id);
}

/** Restore progress AND settings (incl. display name) from a backup code. */
export async function importBackup(code: string): Promise<boolean> {
  try {
    const res = await api<{
      playerId: string;
      progress: { data: string } | null;
      settings: { data: string } | null;
    }>("/backup/import", { method: "POST", body: JSON.stringify({ code }) });
    setPlayerId(res.playerId);
    if (res.progress?.data) {
      const remote = JSON.parse(res.progress.data) as ProgressData;
      progress.replace(mergeProgress(progress.snapshot as ProgressData, remote));
    }
    // Adopt the backup's settings so the display name / profile come back too.
    if (res.settings?.data) {
      const remote = JSON.parse(res.settings.data) as Settings;
      settings.replace(remote);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Boot the sync client: adopt/create a session, pull+merge once, then push on
 * every local change. Fully non-blocking and safe offline.
 */
export function initSync(): void {
  void (async () => {
    await ensureSession();
    await pull();
    unsub?.();
    unsub = progress.onChange(() => schedulePush());
    settings.onChange(() => scheduleSettingsPush());
  })();
}
